import { useTranslation } from "react-i18next";
import { Manager } from "../../../Manager";
import {
  fetchAuditorsWithSuppliers,
  updateAuditorSuppliers,
  fetchSuppliersByIds,
  type AuditorWithSuppliers,
} from "../../../../state/auditor-suppliers";
import { searchSuppliers, type Supplier } from "../../../../state/suppliers";
import { useCallback } from "react";
import { Box, Text } from "@radix-ui/themes";

export function ManageAuditors() {
  const { t } = useTranslation();
  const fetchData = useCallback(() => fetchAuditorsWithSuppliers(), []);

  const handleSubmit = async (
    data: Record<string, unknown>,
    item: AuditorWithSuppliers | null,
  ) => {
    if (!item) {
      // No create - auditors are created via Users manager
      throw new Error(t("auditors.createError"));
    }

    // Update supplier assignments
    const supplierIds = (data.supplier_ids as number[]) ?? [];
    await updateAuditorSuppliers(item.id, supplierIds);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatSuppliers = (suppliers: Supplier[]) => {
    if (suppliers.length === 0) return t("common.none");
    if (suppliers.length <= 2) {
      return suppliers.map((s) => s.name).join(", ");
    }
    return `${suppliers[0].name}, ${suppliers[1].name} +${suppliers.length - 2} more`;
  };

  return (
    <Box p="4">
      <Manager.Root<AuditorWithSuppliers>
        title={t("auditors.title")}
        fetchData={fetchData}
      >
        {/* No CreateButton - auditors are created via Users manager */}

        <Manager.Content>
          <Text size="2" color="gray" mb="4" as="p">
            {t("auditors.description")}
          </Text>
        </Manager.Content>

        <Manager.Table>
          <Manager.Column<AuditorWithSuppliers>
            header={t("users.fullName")}
            accessor={(a) => a.full_name}
          />
          <Manager.Column<AuditorWithSuppliers>
            header={t("users.email")}
            accessor={(a) => a.email ?? "—"}
          />
          <Manager.Column<AuditorWithSuppliers>
            header={t("auditors.assignedSuppliers")}
            accessor={(a) => formatSuppliers(a.suppliers)}
          />
          <Manager.Column<AuditorWithSuppliers>
            header={t("users.createdAt")}
            accessor={(a) => formatDate(a.created_at)}
          />
          <Manager.Actions>
            <Manager.EditAction />
            {/* No DeleteAction - auditors are deleted via Users manager */}
          </Manager.Actions>
        </Manager.Table>

        <Manager.FormDialog<AuditorWithSuppliers>
          title={() => t("auditors.assignSuppliers")}
          description={t("auditors.assignDescription")}
          onSubmit={handleSubmit}
          validate={() => true} // Always valid - empty selection is allowed
        >
          <Manager.MultiAutocompleteField<Supplier>
            name="supplier_ids"
            label="Suppliers"
            placeholder="Search suppliers..."
            getValue={(item) =>
              (item as AuditorWithSuppliers).suppliers.map((s) => s.id)
            }
            searchFn={searchSuppliers}
            fetchByIdsFn={fetchSuppliersByIds}
            getOptionLabel={(s) => s.name}
            getOptionValue={(s) => s.id}
          />
        </Manager.FormDialog>
      </Manager.Root>
    </Box>
  );
}
